// Embedded `ParametricCabinetClass` JSON for the three system classes.
//
// Authoritative source while the `cabinet_classes` ddb collection is
// uninitialised. Once ddb is seeded these documents flow in from the
// server and become editable; the registry merges ddb rows on top of
// the embedded fallback by `id`.
//
// Geometry is faithfully translated from the legacy hardcoded classes
// (BaseCabinet / WallCabinet / TallCabinet in
// `web/src/lib/cabinets/index.ts` as of feat/nested-project-hierarchy).
// Behavioural equivalence is enforced by the snapshot test in
// `seed-equivalence.ts`.

import type { ParametricCabinetClass } from "@/lib/types";

// ── Common 32mm constants exposed as expressions ─────────────────
//
// Defined as fractions so the math (37mm / 25.4) is auditable from
// the JSON, not opaque pre-multiplied floats. Identical params block is
// inlined into each class so designer edits stay local.

const EURO_PARAMS = {
  // Panel & banding
  T: 0.75,             // 3/4" carcass thickness
  BACK_T: 0.25,        // 1/4" back thickness
  REVEAL: "1/16",      // gap between cabinet exterior and door edge
  VGAP: "1/8",         // gap between paired doors
  STR_H: 4,            // stretcher height (cut-list)

  // Back-panel rebate: how deep the side panels are grooved to receive
  // the back. The back panel extends by `rabbet` on its left and right
  // edges to slide into those grooves. Set to 0 to butt the back
  // between the sides instead (no milling, weaker carcass).
  rabbet: "BACK_T",

  // 32mm system, expressed in inches
  MM: "1/25.4",
  SP_PITCH: "32/25.4",  // shelf-pin row pitch
  SP_EDGE:  "37/25.4",  // first/last hole offset
  SP_FRONT_OFF: "37/25.4",
  SP_BACK_OFF:  "37/25.4",
  SP_DIAM:  "5/25.4",
  SP_DEPTH: "13/25.4",
  HC_DIAM:  "35/25.4",
  HC_DEPTH: "13/25.4",
  HC_EDGE:  "22/25.4",
  HC_TOP_BOTTOM: "76/25.4",
} as const;

// ── BaseCabinet ─────────────────────────────────────────────────

export const BASE_CABINET: ParametricCabinetClass = {
  id: "BaseCabinet",
  label: "Base cabinet",
  category: "base",
  anchor: "floor",
  schemaVersion: 4,
  system: true,
  locked: false,
  defaults: { w: 36, h: 34.5, d: 24 },
  bounds:   { w: [9, 48], h: [30, 36], d: [18, 27] },
  params: {
    ...EURO_PARAMS,
    TOE_KICK_H: 4,
    TOE_KICK_D: 3,
    /** Cabinet carcass height (excludes the toe kick). */
    carcassH: "H - TOE_KICK_H",
    /** Inner span between side panels. */
    interiorW: "W - 2*T",
    /** Inner depth in front of the back. */
    interiorD: "D - BACK_T",
    /** Total horizontal door run, less reveal margin. */
    doorTotal: "W - 2*REVEAL",
    /** Width of one door in a paired layout. */
    halfDoorW: "(doorTotal - VGAP) / 2",
    /** Vertical span of doors. */
    doorH:     "carcassH - 2*REVEAL",
    shelfCount: 1,
  },
  parts: [
    {
      id: "left_side", role: "side_left", name: "Left side",
      box: { min: ["0",     "TOE_KICK_H", "0"], max: ["T", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "D", h: "carcassH - T" },
      drilling: [
        { kind: "shelfpin_rows" },
        // Back-panel rebate: vertical slot at the back edge of the
        // inside face. `offset = width/2` keeps the slot's back wall
        // flush with the part's back edge (X=0 on this face).
        { kind: "groove_back", width: "BACK_T", depth: "rabbet" },
      ],
    },
    {
      id: "right_side", role: "side_right", name: "Right side",
      box: { min: ["W - T", "TOE_KICK_H", "0"], max: ["W", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "D", h: "carcassH - T" },
      drilling: [
        { kind: "shelfpin_rows" },
        { kind: "groove_back", width: "BACK_T", depth: "rabbet" },
      ],
    },
    {
      id: "bottom", role: "bottom", name: "Bottom",
      box: { min: ["T", "TOE_KICK_H",     "BACK_T"], max: ["W - T", "TOE_KICK_H + T", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "interiorD" },
      joinery: {
        xMin: { kind: "butt", matesWith: "left_side",  fastener: { kind: "screw" } },
        xMax: { kind: "butt", matesWith: "right_side", fastener: { kind: "screw" } },
      },
    },
    {
      // Back panel extends into the side rebates by `rabbet` on each
      // edge — geometry author writes the offset; sides carry the
      // matching groove milling above.
      id: "back", role: "back", name: "Back",
      box: { min: ["T - rabbet", "TOE_KICK_H + T", "0"], max: ["W - T + rabbet", "H - T", "BACK_T"] },
      thickness: "BACK_T", materialSlot: "back", grain: "none",
      cut: { w: "interiorW + 2*rabbet", h: "carcassH - 2*T" },
      joinery: {
        xMin: { kind: "rabbet", matesWith: "left_side",  depth: "rabbet" },
        xMax: { kind: "rabbet", matesWith: "right_side", depth: "rabbet" },
      },
    },
    {
      id: "str_back", role: "stretcher_back", name: "Top stretcher (back)",
      box: { min: ["T", "H - T", "BACK_T"], max: ["W - T", "H", "BACK_T + T"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "STR_H" },
    },
    {
      id: "str_front", role: "stretcher_front", name: "Top stretcher (front)",
      box: { min: ["T", "H - T", "D - T"], max: ["W - T", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "STR_H" },
    },
    {
      id: "shelf", role: "shelf", name: "Shelf ${i+1}",
      repeat: { count: "shelfCount", var: "i" },
      box: {
        min: ["T",     "TOE_KICK_H + carcassH * (i + 1) / (shelfCount + 1)",       "BACK_T"],
        max: ["W - T", "TOE_KICK_H + carcassH * (i + 1) / (shelfCount + 1) + T",   "D - T"],
      },
      thickness: "T", materialSlot: "shelf", grain: "length",
      cut: { w: "interiorW", h: "interiorD - 0.5" },
    },
    {
      id: "toe_kick", role: "toe_kick", name: "Toe kick",
      enabled: "TOE_KICK_H > 0",
      box: { min: ["0", "0", "D - TOE_KICK_D - T"], max: ["W", "TOE_KICK_H", "D - TOE_KICK_D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "W", h: "TOE_KICK_H" },
    },
    // Single door if W ≤ 21″; otherwise the pair below.
    {
      id: "door", role: "door", name: "Door",
      enabled: "W <= 21",
      box: { min: ["REVEAL", "TOE_KICK_H + REVEAL", "D"], max: ["W - REVEAL", "H - REVEAL", "D + T"] },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "doorTotal", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_left", role: "door_left", name: "Door (left)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL",              "TOE_KICK_H + REVEAL", "D"],
        max: ["REVEAL + halfDoorW",  "H - REVEAL",          "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_right", role: "door_right", name: "Door (right)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL + halfDoorW + VGAP", "TOE_KICK_H + REVEAL", "D"],
        max: ["W - REVEAL",                "H - REVEAL",          "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "right" }],
    },
  ],
};

// ── WallCabinet ─────────────────────────────────────────────────

export const WALL_CABINET: ParametricCabinetClass = {
  id: "WallCabinet",
  label: "Wall cabinet",
  category: "wall",
  anchor: "wall",
  schemaVersion: 4,
  system: true,
  locked: false,
  defaults: { w: 30, h: 30, d: 12 },
  bounds:   { w: [9, 48], h: [12, 42], d: [9, 15] },
  params: {
    ...EURO_PARAMS,
    /** Wall cabinets have no toe kick — kept as a param so designer can
     *  add one by overriding TOE_KICK_H>0 per cabinet/class. */
    TOE_KICK_H: 0,
    TOE_KICK_D: 0,
    carcassH: "H - TOE_KICK_H",
    interiorW: "W - 2*T",
    interiorD: "D - BACK_T",
    doorTotal: "W - 2*REVEAL",
    halfDoorW: "(doorTotal - VGAP) / 2",
    doorH:     "carcassH - 2*REVEAL",
    shelfCount: 1,
  },
  parts: [
    {
      id: "left_side", role: "side_left", name: "Left side",
      box: { min: ["0",     "TOE_KICK_H", "0"], max: ["T", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "D", h: "carcassH - T" },
      drilling: [
        { kind: "shelfpin_rows" },
        { kind: "groove_back", width: "BACK_T", depth: "rabbet" },
      ],
    },
    {
      id: "right_side", role: "side_right", name: "Right side",
      box: { min: ["W - T", "TOE_KICK_H", "0"], max: ["W", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "D", h: "carcassH - T" },
      drilling: [
        { kind: "shelfpin_rows" },
        { kind: "groove_back", width: "BACK_T", depth: "rabbet" },
      ],
    },
    {
      id: "bottom", role: "bottom", name: "Bottom",
      box: { min: ["T", "TOE_KICK_H",     "BACK_T"], max: ["W - T", "TOE_KICK_H + T", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "interiorD" },
      joinery: {
        xMin: { kind: "butt", matesWith: "left_side",  fastener: { kind: "screw" } },
        xMax: { kind: "butt", matesWith: "right_side", fastener: { kind: "screw" } },
      },
    },
    {
      id: "back", role: "back", name: "Back",
      box: { min: ["T - rabbet", "TOE_KICK_H + T", "0"], max: ["W - T + rabbet", "H - T", "BACK_T"] },
      thickness: "BACK_T", materialSlot: "back", grain: "none",
      cut: { w: "interiorW + 2*rabbet", h: "carcassH - 2*T" },
      joinery: {
        xMin: { kind: "rabbet", matesWith: "left_side",  depth: "rabbet" },
        xMax: { kind: "rabbet", matesWith: "right_side", depth: "rabbet" },
      },
    },
    {
      id: "str_back", role: "stretcher_back", name: "Top stretcher (back)",
      box: { min: ["T", "H - T", "BACK_T"], max: ["W - T", "H", "BACK_T + T"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "STR_H" },
    },
    {
      id: "str_front", role: "stretcher_front", name: "Top stretcher (front)",
      box: { min: ["T", "H - T", "D - T"], max: ["W - T", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "STR_H" },
    },
    {
      id: "shelf", role: "shelf", name: "Shelf ${i+1}",
      repeat: { count: "shelfCount", var: "i" },
      box: {
        min: ["T",     "TOE_KICK_H + carcassH * (i + 1) / (shelfCount + 1)",       "BACK_T"],
        max: ["W - T", "TOE_KICK_H + carcassH * (i + 1) / (shelfCount + 1) + T",   "D - T"],
      },
      thickness: "T", materialSlot: "shelf", grain: "length",
      cut: { w: "interiorW", h: "interiorD - 0.5" },
    },
    {
      id: "toe_kick", role: "toe_kick", name: "Toe kick",
      enabled: "TOE_KICK_H > 0",
      box: { min: ["0", "0", "D - TOE_KICK_D - T"], max: ["W", "TOE_KICK_H", "D - TOE_KICK_D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "W", h: "TOE_KICK_H" },
    },
    {
      id: "door", role: "door", name: "Door",
      enabled: "W <= 21",
      box: { min: ["REVEAL", "TOE_KICK_H + REVEAL", "D"], max: ["W - REVEAL", "H - REVEAL", "D + T"] },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "doorTotal", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_left", role: "door_left", name: "Door (left)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL",              "TOE_KICK_H + REVEAL", "D"],
        max: ["REVEAL + halfDoorW",  "H - REVEAL",          "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_right", role: "door_right", name: "Door (right)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL + halfDoorW + VGAP", "TOE_KICK_H + REVEAL", "D"],
        max: ["W - REVEAL",                "H - REVEAL",          "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "right" }],
    },
  ],
};

// ── TallCabinet ─────────────────────────────────────────────────
//
// One tall carcass (so shelf-pin rows go on full-height sides), plus
// two pairs of doors stacked: lower at y∈[TOE_KICK_H, TOE_KICK_H+halfH]
// and upper at y∈[TOE_KICK_H+halfH+VGAP, TOE_KICK_H+halfH+VGAP+halfH].
// `halfH = carcassH / 2 - VGAP / 2`. Wider than 21″ so always paired.

export const TALL_CABINET: ParametricCabinetClass = {
  id: "TallCabinet",
  label: "Tall cabinet (pantry)",
  category: "tall",
  anchor: "floor",
  schemaVersion: 4,
  system: true,
  locked: false,
  defaults: { w: 24, h: 84, d: 24 },
  bounds:   { w: [12, 36], h: [60, 96], d: [12, 27] },
  params: {
    ...EURO_PARAMS,
    TOE_KICK_H: 4,
    TOE_KICK_D: 3,
    carcassH: "H - TOE_KICK_H",
    interiorW: "W - 2*T",
    interiorD: "D - BACK_T",
    halfH: "carcassH / 2 - VGAP / 2",
    doorTotal: "W - 2*REVEAL",
    halfDoorW: "(doorTotal - VGAP) / 2",
    doorH: "halfH - 2*REVEAL",
    shelfCount: 3,
    /** Y offset of the upper door pair. */
    topDoorY:  "TOE_KICK_H + halfH + VGAP",
  },
  parts: [
    {
      id: "left_side", role: "side_left", name: "Left side",
      box: { min: ["0",     "TOE_KICK_H", "0"], max: ["T", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "D", h: "carcassH - T" },
      drilling: [
        { kind: "shelfpin_rows" },
        { kind: "groove_back", width: "BACK_T", depth: "rabbet" },
      ],
    },
    {
      id: "right_side", role: "side_right", name: "Right side",
      box: { min: ["W - T", "TOE_KICK_H", "0"], max: ["W", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "D", h: "carcassH - T" },
      drilling: [
        { kind: "shelfpin_rows" },
        { kind: "groove_back", width: "BACK_T", depth: "rabbet" },
      ],
    },
    {
      id: "bottom", role: "bottom", name: "Bottom",
      box: { min: ["T", "TOE_KICK_H",     "BACK_T"], max: ["W - T", "TOE_KICK_H + T", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "interiorD" },
      joinery: {
        xMin: { kind: "butt", matesWith: "left_side",  fastener: { kind: "screw" } },
        xMax: { kind: "butt", matesWith: "right_side", fastener: { kind: "screw" } },
      },
    },
    {
      id: "back", role: "back", name: "Back",
      box: { min: ["T - rabbet", "TOE_KICK_H + T", "0"], max: ["W - T + rabbet", "H - T", "BACK_T"] },
      thickness: "BACK_T", materialSlot: "back", grain: "none",
      cut: { w: "interiorW + 2*rabbet", h: "carcassH - 2*T" },
      joinery: {
        xMin: { kind: "rabbet", matesWith: "left_side",  depth: "rabbet" },
        xMax: { kind: "rabbet", matesWith: "right_side", depth: "rabbet" },
      },
    },
    {
      id: "str_back", role: "stretcher_back", name: "Top stretcher (back)",
      box: { min: ["T", "H - T", "BACK_T"], max: ["W - T", "H", "BACK_T + T"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "STR_H" },
    },
    {
      id: "str_front", role: "stretcher_front", name: "Top stretcher (front)",
      box: { min: ["T", "H - T", "D - T"], max: ["W - T", "H", "D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "interiorW", h: "STR_H" },
    },
    {
      id: "shelf", role: "shelf", name: "Shelf ${i+1}",
      repeat: { count: "shelfCount", var: "i" },
      box: {
        min: ["T",     "TOE_KICK_H + carcassH * (i + 1) / (shelfCount + 1)",       "BACK_T"],
        max: ["W - T", "TOE_KICK_H + carcassH * (i + 1) / (shelfCount + 1) + T",   "D - T"],
      },
      thickness: "T", materialSlot: "shelf", grain: "length",
      cut: { w: "interiorW", h: "interiorD - 0.5" },
    },
    {
      id: "toe_kick", role: "toe_kick", name: "Toe kick",
      enabled: "TOE_KICK_H > 0",
      box: { min: ["0", "0", "D - TOE_KICK_D - T"], max: ["W", "TOE_KICK_H", "D - TOE_KICK_D"] },
      thickness: "T", materialSlot: "carcass", grain: "length",
      cut: { w: "W", h: "TOE_KICK_H" },
    },
    // ── Lower doors (paired since W > 21 by bounds[0]=12 but min wall is 12; really wider variants) ──
    {
      id: "door_bot", role: "door", name: "Door (lower)",
      enabled: "W <= 21",
      box: { min: ["REVEAL", "TOE_KICK_H + REVEAL", "D"], max: ["W - REVEAL", "TOE_KICK_H + halfH - REVEAL", "D + T"] },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "doorTotal", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_left_bot", role: "door_left", name: "Door lower (left)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL",             "TOE_KICK_H + REVEAL",          "D"],
        max: ["REVEAL + halfDoorW", "TOE_KICK_H + halfH - REVEAL",  "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_right_bot", role: "door_right", name: "Door lower (right)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL + halfDoorW + VGAP", "TOE_KICK_H + REVEAL",         "D"],
        max: ["W - REVEAL",                "TOE_KICK_H + halfH - REVEAL", "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "right" }],
    },
    // ── Upper doors ──
    {
      id: "door_top", role: "door", name: "Door (upper)",
      enabled: "W <= 21",
      box: { min: ["REVEAL", "topDoorY + REVEAL", "D"], max: ["W - REVEAL", "topDoorY + halfH - REVEAL", "D + T"] },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "doorTotal", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_left_top", role: "door_left", name: "Door upper (left)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL",             "topDoorY + REVEAL",          "D"],
        max: ["REVEAL + halfDoorW", "topDoorY + halfH - REVEAL",  "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "left" }],
    },
    {
      id: "door_right_top", role: "door_right", name: "Door upper (right)",
      enabled: "W > 21",
      box: {
        min: ["REVEAL + halfDoorW + VGAP", "topDoorY + REVEAL",         "D"],
        max: ["W - REVEAL",                "topDoorY + halfH - REVEAL", "D + T"],
      },
      thickness: "T", materialSlot: "door", grain: "length",
      cut: { w: "halfDoorW", h: "doorH" },
      drilling: [{ kind: "hinge_cups", hingeSide: "right" }],
    },
  ],
};

export const SYSTEM_CLASSES: ParametricCabinetClass[] = [
  BASE_CABINET,
  WALL_CABINET,
  TALL_CABINET,
];

export const SYSTEM_CLASS_BY_ID: Record<string, ParametricCabinetClass> = {
  BaseCabinet: BASE_CABINET,
  WallCabinet: WALL_CABINET,
  TallCabinet: TALL_CABINET,
};
